import { useCallback } from 'react';
import { useStore } from 'jotai';
import {
  createGridClipboardPayload,
  readGridClipboard,
  useCopiedOutline,
  writeGridClipboard,
  type DataGridProps,
} from 'components/DataGrid';
import { usePlanMutations } from '../../state';
import type { GroupPatch, RowPatch } from '../../diff';
import type { GridSummaryRow, RowOrGroup } from '../../types';
import { isPlanCellPayload, PLAN_GRID_ID, type PlanClipboardData, type PlanColumn } from '../clipboard';

type PlanClipboardProps = Pick<
  DataGridProps<RowOrGroup, GridSummaryRow>,
  'copiedOutline' | 'onClipboardCopy' | 'onClipboardPaste' | 'onCopiedOutlineClear'
>;

/**
 * Clipboard + marching-ants wiring for the plan grid. Cells participate by
 * declaring `clipboard` on their branch (see createPlanColumn); everything
 * else bails silently, leaving the clipboard untouched on copy and skipped on
 * paste. Paste accepts only our own payload — external scalars carry no field
 * identity — anchors the copied rectangle at the target cell, and lands all
 * resulting patches as a single plan/update diff, one upsert per row.
 */
export function usePlanClipboard(): PlanClipboardProps {
  const { outline, copyId, setOutline } = useCopiedOutline();
  const { update } = usePlanMutations();
  const store = useStore();

  const onClipboardCopy = useCallback<NonNullable<PlanClipboardProps['onClipboardCopy']>>(
    ({ rows, columns }, event) => {
      const cells = rows.map((row) => columns.map((column) => (column as PlanColumn).clipboardCell?.(row) ?? null));
      // nothing participates: keep prior clipboard content
      if (!cells.some((cellRow) => cellRow.some((cell) => cell !== null))) return;

      const payload = createGridClipboardPayload<PlanClipboardData>({
        gridId: PLAN_GRID_ID,
        columnKeys: columns.map((column) => column.key),
        rowKeys: rows.map((row) => row._key),
        data: cells.map((cellRow) => cellRow.map((cell) => (cell ? cell.copy() : null))),
      });
      const scalars = cells.map((cellRow) => cellRow.map((cell) => cell?.scalar ?? ''));
      writeGridClipboard(event.clipboardData, payload, scalars);
      setOutline({ copyId: payload.copyId, rowKeys: payload.rowKeys, columnKeys: payload.columnKeys });
    },
    [setOutline],
  );

  const onClipboardPaste = useCallback<NonNullable<PlanClipboardProps['onClipboardPaste']>>(
    ({ target, rows, columns }, event) => {
      // Cells resolve pasted values against the store asynchronously, but the plan
      // grid has no rectangle selection to move afterwards, so nothing is returned
      // to the grid. `readGridClipboard` runs synchronously (before the first
      // await) so `event.clipboardData` is still live; the rest can settle later.
      const result = readGridClipboard(event.clipboardData);
      if (result.kind !== 'grid' || result.payload.gridId !== PLAN_GRID_ID) return;
      const { payload } = result;

      void (async () => {
        // The clipboard was overwritten by a later copy elsewhere — the payload is
        // still valid, but the local ants no longer describe it.
        if (payload.copyId !== copyId) setOutline(null);

        const data: unknown = payload.data;
        if (!Array.isArray(data)) return;

        // One upsert per row regardless of rectangle size — the backend has no
        // range concept, it only ever sees a plain diff. Group patches land in
        // `groups`, row patches in `rows`; both are sent in a single plan/update.
        const rowPatches = new Map<number, RowPatch>();
        const groupPatches = new Map<number, GroupPatch>();
        for (let r = 0; r < payload.rowKeys.length; r++) {
          const targetRow = rows[target.rowIdx + r];
          const dataRow: unknown = data[r];
          if (targetRow === undefined || !Array.isArray(dataRow)) continue;

          for (let c = 0; c < payload.columnKeys.length; c++) {
            const source: unknown = dataRow[c];
            // null / malformed source cells are skipped but keep their offset
            if (!isPlanCellPayload(source)) continue;
            const targetColumn = columns[target.idx + c] as PlanColumn | undefined;
            const cell = targetColumn?.clipboardCell?.(targetRow);
            if (cell == null) continue;

            const patch = await cell.paste(source, { store });
            if (patch === null) continue;

            // The cell was resolved against this target row, so the patch shape
            // tracks its kind — row patches for rows, group patches for groups.
            if (targetRow._isGroup) {
              const p = patch as GroupPatch;
              const prev = groupPatches.get(targetRow.id);
              groupPatches.set(targetRow.id, prev === undefined ? p : { ...prev, ...p });
            } else {
              const p = patch as RowPatch;
              const prev = rowPatches.get(targetRow.id);
              rowPatches.set(targetRow.id, prev === undefined ? p : { ...prev, ...p });
            }
          }
        }

        if (rowPatches.size === 0 && groupPatches.size === 0) return;
        void update({
          ...(rowPatches.size > 0 && { rows: { upsert: [...rowPatches.values()] } }),
          ...(groupPatches.size > 0 && { groups: { upsert: [...groupPatches.values()] } }),
        }).then((res) => res.unwrapActionError());
      })();
    },
    [copyId, setOutline, store, update],
  );

  const onCopiedOutlineClear = useCallback(() => setOutline(null), [setOutline]);

  return { copiedOutline: outline, onClipboardCopy, onClipboardPaste, onCopiedOutlineClear };
}
